import React from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, FileSpreadsheet, CheckCircle2, TrendingUp, MessageSquare, DollarSign, Link as LinkIcon, Globe, Users, Mail, BadgeCheck, ChevronDown } from 'lucide-react';
// Platform Icons
const TTIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3.5 h-3.5 inline-block" data-id="element-1361" />;
const IGIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3.5 h-3.5 inline-block" data-id="element-1362" />;
const WindowBar = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/70" data-id="element-1363">
    <div className="flex items-center gap-1.5" data-id="element-1364">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" data-id="element-1365" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" data-id="element-1366" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" data-id="element-1367" />
    </div>
    <span className="ml-2 text-xs font-semibold text-gray-600" data-id="element-1368">{title}</span>
    {subtitle && <span className="ml-auto text-[10px] text-gray-400" data-id="element-1369">{subtitle}</span>}
  </div>;
const floatY = (delay = 0, distance = 8) => ({
  animate: {
    y: [0, -distance, 0]
  },
  transition: {
    duration: 4,
    repeat: Infinity,
    ease: 'easeInOut' as const,
    delay
  }
});
// Shared generic creator data (photos + names + niches) reused across mockups.
const CREATORS = [{
  name: 'Elena Rodriguez',
  niche: 'Fashion',
  fol: '1.2M',
  views: '340K',
  eng: '4.2%',
  photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Marcus Chen',
  niche: 'Tech',
  fol: '850K',
  views: '120K',
  eng: '5.1%',
  photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Sarah Jenkins',
  niche: 'Lifestyle',
  fol: '450K',
  views: '89K',
  eng: '3.8%',
  photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'David Kim',
  niche: 'Gaming',
  fol: '2.1M',
  views: '800K',
  eng: '6.4%',
  photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Aisha Kohl',
  niche: 'Beauty',
  fol: '640K',
  views: '150K',
  eng: '4.9%',
  photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=80&h=80&q=80'
}];
export const LayeredRosterMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1370">
      <div className="absolute -inset-6 bg-gradient-to-tr from-orange-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1371" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1372">
        <WindowBar title="My Roster" data-id="element-1373" />

        {/* Toolbar */}
        <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-white" data-id="element-1374">
          <div className="flex items-center gap-2" data-id="element-1375">
            <div className="relative w-48" data-id="element-1376">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1377" />
              <input type="text" placeholder="Search roster..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[11px] outline-none bg-gray-50" data-id="element-1378" />
            </div>
            <button className="flex items-center gap-1 border border-gray-200 bg-white text-gray-600 px-2.5 py-1.5 rounded-md text-[11px] font-medium" data-id="element-1379">
              <Filter size={12} data-id="element-1380" /> Filter
            </button>
          </div>
          <button className="bg-gray-900 text-white px-3 py-1.5 rounded-md text-[11px] font-bold" data-id="element-1381">
            + Add Creator
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden bg-gray-50/30" data-id="element-1382">
          <table className="w-full text-left text-[11px] whitespace-nowrap" data-id="element-1383">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase" data-id="element-1384">
              <tr data-id="element-1385">
                <th className="px-4 py-2.5 font-semibold" data-id="element-1386">Influencer</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-1387">Niche</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-1388">Followers</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-1389">Avg Views</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-1390">Eng. Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white" data-id="element-1391">
              {CREATORS.map((row, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-1392">
                  <td className="px-4 py-2.5 font-medium text-gray-900 flex items-center gap-2" data-id="element-1393">
                    <img src={row.photo} alt={row.name} className="w-5 h-5 rounded-full object-cover bg-gray-200" data-id="element-1394" />
                    {row.name}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600" data-id="element-1395">{row.niche}</td>
                  <td className="px-4 py-2.5 text-gray-900 font-medium" data-id="element-1396">
                    {row.fol}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600" data-id="element-1397">{row.views}</td>
                  <td className="px-4 py-2.5 text-emerald-600 font-medium" data-id="element-1398">
                    {row.eng}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating Bulk Upload */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-4 sm:-right-6 z-20" data-id="element-1399">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-1400">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-1401">
            <div className="p-1.5 bg-green-50 rounded-md" data-id="element-1402">
              <FileSpreadsheet size={14} className="text-green-600" data-id="element-1403" />
            </div>
            <p className="text-[11px] font-bold text-gray-900" data-id="element-1404">Bulk Upload</p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-1405">
            Import your entire roster in seconds using Google Sheets.
          </p>
        </motion.div>
      </motion.div>

      {/* Floating Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-5 -left-4 sm:-left-6 z-20" data-id="element-1406">
        <motion.div {...floatY(0.9)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-1407">
          <p className="text-[10px] font-bold text-gray-900 mb-2 flex items-center gap-1" data-id="element-1408">
            <Filter size={10} className="text-zano-pink" data-id="element-1409" /> Active Filters
          </p>
          <div className="flex flex-wrap gap-1.5" data-id="element-1410">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-1411">
              Followers &gt; 500K
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-1412">
              US-based
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-1413">
              Tech & Gaming
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredSubmissionsMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1414">
      <div className="absolute -inset-6 bg-gradient-to-tr from-sky-100/40 via-zano-lavender/30 to-zano-pink/10 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1415" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1416">
        <WindowBar title="Submit to Casting Call" subtitle="Summer Skincare Launch" data-id="element-1417" />

        <div className="p-4 space-y-4" data-id="element-1418">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-id="element-1419">
            <h4 className="text-xs font-bold text-gray-900 mb-1" data-id="element-1420">
              Select from your roster
            </h4>
            <div className="flex items-center gap-2 mb-3" data-id="element-1421">
              <div className="relative flex-1" data-id="element-1422">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1423" />
                <input type="text" placeholder="Search..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[10px] outline-none bg-white" data-id="element-1424" />
              </div>
              <button className="flex items-center gap-1 border border-zano-pink/30 bg-zano-blush text-zano-pink px-2 py-1.5 rounded-md text-[10px] font-medium" data-id="element-1425">
                <Filter size={10} data-id="element-1426" /> Filter
              </button>
            </div>

            <div className="space-y-2" data-id="element-1427">
              <div className="flex items-center justify-between bg-white border border-zano-pink/40 rounded-lg p-2 shadow-sm" data-id="element-1428">
                <div className="flex items-center gap-2" data-id="element-1429">
                  <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=40&h=40" alt="" className="w-7 h-7 rounded-full object-cover" data-id="element-1430" />
                  <div data-id="element-1431">
                    <p className="text-[11px] font-bold text-gray-900" data-id="element-1432">
                      Elena Rodriguez
                    </p>
                    <p className="text-[9px] text-gray-500" data-id="element-1433">
                      1.2M Followers • 340K Avg Views
                    </p>
                  </div>
                </div>
                <CheckCircle2 size={16} className="text-zano-pink" data-id="element-1434" />
              </div>
            </div>
          </div>

          <div className="space-y-3" data-id="element-1435">
            <div data-id="element-1436">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1437">
                Your Quote
              </label>
              <div className="mt-1 relative" data-id="element-1438">
                <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1439" />
                <input type="text" value="2,500" readOnly className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-md text-xs font-medium bg-gray-50" data-id="element-1440" />
              </div>
            </div>
            <div data-id="element-1441">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1442">
                Notes for Brand
              </label>
              <textarea readOnly className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-[11px] text-gray-700 bg-gray-50 h-16 resize-none" value="Elena has a highly engaged US female audience. She recently did a skincare campaign that performed 2x above average." data-id="element-1443" />
            </div>
            <button className="w-full bg-zano-pink text-white rounded-md py-2 text-xs font-bold shadow-sm" data-id="element-1444">
              Submit Influencer
            </button>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-3 -left-4 sm:-left-6 z-20" data-id="element-1445">
        <motion.div {...floatY(0.3, 6)} className="bg-white rounded-xl border border-gray-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-1446">
          <p className="text-[10px] font-bold text-gray-900 mb-1.5 flex items-center gap-1" data-id="element-1447">
            <Filter size={10} className="text-zano-pink" data-id="element-1448" /> Roster Filter
            Applied
          </p>
          <div className="space-y-1" data-id="element-1449">
            <div className="flex justify-between text-[9px]" data-id="element-1450">
              <span className="text-gray-500" data-id="element-1451">Followers</span>
              <span className="font-semibold text-gray-900" data-id="element-1452">&gt; 50K</span>
            </div>
            <div className="flex justify-between text-[9px]" data-id="element-1453">
              <span className="text-gray-500" data-id="element-1454">Avg Views</span>
              <span className="font-semibold text-gray-900" data-id="element-1455">&gt; 100K</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-6 -right-4 sm:-right-6 z-20" data-id="element-1456">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-1457">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1458">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1459" />
            <p className="text-[10px] font-bold text-gray-900" data-id="element-1460">Custom Pitch</p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-1461">
            Add your rate and notes directly to the submission to win the deal.
          </p>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredMediaKitMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1462">
      <div className="absolute -inset-6 bg-gradient-to-tr from-purple-100/40 via-zano-lavender/30 to-blue-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1463" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1464">
        <WindowBar title="Public Media Kit" data-id="element-1465" />

        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-1466">
          <div className="flex items-center gap-3" data-id="element-1467">
            <div className="w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center text-xs font-bold shadow-sm" data-id="element-1468">
              ABC
            </div>
            <div data-id="element-1469">
              <h3 className="text-sm font-bold text-gray-900" data-id="element-1470">
                ABC Talent Agency
              </h3>
              <p className="text-[11px] text-gray-500" data-id="element-1471">Exclusive Roster</p>
            </div>
          </div>
          <button className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-md text-[11px] font-bold shadow-sm" data-id="element-1472">
            <LinkIcon size={12} data-id="element-1473" /> Share Link
          </button>
        </div>

        <div className="p-4 flex-1 bg-white" data-id="element-1474">
          <div className="flex items-center gap-2 mb-3" data-id="element-1475">
            <div className="relative flex-1" data-id="element-1476">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1477" />
              <input type="text" placeholder="Brands can search your roster..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[10px] bg-gray-50" readOnly data-id="element-1478" />
            </div>
            <button className="flex items-center gap-1 border border-gray-200 bg-white text-gray-600 px-2 py-1.5 rounded-md text-[10px] font-medium" data-id="element-1479">
              <Filter size={10} data-id="element-1480" /> Filters
            </button>
          </div>

          <div className="border border-gray-100 rounded-xl overflow-hidden" data-id="element-1481">
            <table className="w-full text-left text-[11px] whitespace-nowrap" data-id="element-1482">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase" data-id="element-1483">
                <tr data-id="element-1484">
                  <th className="px-3 py-2 font-semibold" data-id="element-1485">Creator</th>
                  <th className="px-3 py-2 font-semibold" data-id="element-1486">Niche</th>
                  <th className="px-3 py-2 font-semibold" data-id="element-1487">
                    <TTIco data-id="element-1488" />
                  </th>
                  <th className="px-3 py-2 font-semibold text-right" data-id="element-1489">Rates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white" data-id="element-1490">
                {CREATORS.slice(0, 4).map((c, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-1491">
                    <td className="px-3 py-2 font-medium text-gray-900 flex items-center gap-2" data-id="element-1492">
                      <img src={c.photo} alt={c.name} className="w-6 h-6 rounded-full object-cover bg-gray-200" data-id="element-1493" />
                      {c.name}
                    </td>
                    <td className="px-3 py-2 text-gray-600" data-id="element-1494">{c.niche}</td>
                    <td className="px-3 py-2 text-gray-900 font-medium" data-id="element-1495">
                      {c.fol}
                    </td>
                    <td className="px-3 py-2 text-right" data-id="element-1496">
                      <span className="text-[10px] font-semibold text-emerald-600" data-id="element-1497">
                        View
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-4 sm:-right-6 z-20" data-id="element-1498">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-1499">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1500">
            <Globe size={12} className="text-blue-500" data-id="element-1501" />
            <p className="text-[11px] font-bold text-gray-900" data-id="element-1502">
              Public & Shareable
            </p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-1503">
            Send your live media kit to brands. Built-in filters help you upsell
            more talent.
          </p>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-4 -left-4 sm:-left-6 z-20" data-id="element-1504">
        <motion.div {...floatY(0.8)} className="flex items-center gap-2 bg-gray-900 text-white rounded-full border border-gray-700 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.3)] px-3 py-2" data-id="element-1505">
          <CheckCircle2 size={14} className="text-emerald-400" data-id="element-1506" />
          <span className="text-[11px] font-medium" data-id="element-1507">
            Link copied to clipboard
          </span>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredDiscoverabilityMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1508">
      <div className="absolute -inset-6 bg-gradient-to-tr from-amber-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1509" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1510">
        <WindowBar title="Brand View: Roster Database" data-id="element-1511" />

        <div className="p-4 bg-gray-50/50 border-b border-gray-100" data-id="element-1512">
          <div className="relative flex-1" data-id="element-1513">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zano-pink" data-id="element-1514" />
            <div className="w-full pl-9 pr-3 py-2 border-2 border-zano-pink/40 rounded-lg text-xs font-medium text-gray-900 bg-white shadow-sm" data-id="element-1515">
              fashion
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5" data-id="element-1516">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white border border-gray-200 text-gray-700" data-id="element-1517">
              <Globe size={10} className="text-gray-400" data-id="element-1518" /> Location:
              <span className="font-semibold text-gray-900" data-id="element-1519">United States</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white border border-gray-200 text-gray-700" data-id="element-1520">
              Platform: <TTIco data-id="element-1521" />
              <span className="font-semibold text-gray-900" data-id="element-1522">TikTok</span>
            </span>
          </div>
        </div>

        <div className="p-4 space-y-3" data-id="element-1523">
          <div className="border border-zano-pink/30 bg-zano-blush/20 rounded-xl p-3 shadow-sm" data-id="element-1524">
            <div className="flex items-center gap-3 mb-3" data-id="element-1525">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=60&h=60" className="w-12 h-12 rounded-full object-cover" alt="" data-id="element-1526" />
              <div data-id="element-1527">
                <h4 className="text-sm font-bold text-gray-900" data-id="element-1528">
                  Elena Rodriguez
                </h4>
                <p className="text-[10px] text-gray-500" data-id="element-1529">Los Angeles, CA</p>
              </div>
            </div>
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-2.5 py-2" data-id="element-1530">
              <div className="flex items-center gap-2" data-id="element-1531">
                <div className="w-6 h-6 bg-gray-900 text-white rounded flex items-center justify-center text-[8px] font-bold" data-id="element-1532">
                  ABC
                </div>
                <span className="text-[11px] font-bold text-gray-900" data-id="element-1533">
                  ABC Talent Agency
                </span>
              </div>
              <button className="bg-zano-pink text-white px-3 py-1 rounded text-[10px] font-bold" data-id="element-1534">
                Contact Manager
              </button>
            </div>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-4 -right-3 sm:-right-5 z-20" data-id="element-1535">
        <motion.div {...floatY(0.3, 6)} className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-1536">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-1537">
            <Users size={14} className="text-blue-600" data-id="element-1538" />
          </div>
          <div data-id="element-1539">
            <p className="text-[11px] font-bold text-gray-900" data-id="element-1540">
              Get Discovered
            </p>
            <p className="text-[9px] text-gray-500" data-id="element-1541">
              Brands search the database daily.
            </p>
          </div>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-6 -left-4 sm:-left-6 z-20" data-id="element-1542">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-56" data-id="element-1543">
          <div className="flex items-center gap-2 mb-2" data-id="element-1544">
            <div className="p-1.5 bg-zano-blush rounded-md" data-id="element-1545">
              <Mail size={12} className="text-zano-pink" data-id="element-1546" />
            </div>
            <p className="text-[11px] font-bold text-gray-900" data-id="element-1547">
              New Inbound Inquiry
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded p-2" data-id="element-1548">
            <p className="text-[10px] text-gray-600 line-clamp-2" data-id="element-1549">
              "Hi, we saw Elena in the Zano database and would love to book her
              for our upcoming campaign..."
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};